import { useEffect, useRef, useState } from "react";
import { addWork } from "@/lib/ccApi";
import { cn } from "@/lib/utils";

// Typed intake: Jeff asks in plain words; it lands on the board as a new idea
// and Kit picks it up. Built for dictation (Jeff, 2026-10-27, 3 failed dumps):
// a big auto-growing textarea that takes whole spoken/pasted dumps — hard cap
// 20k chars — and if a paste lands bigger, the box keeps the first 20k and
// TELLS him how much was cut (the old one-line input swallowed it silently).
const LIMIT = 20000;

export const AddWorkBar = ({ onAdded, registerFill, registerAttachIds, tall }) => {
  // Uncontrolled textarea (Jeff 10/30: VoiceInk dictation longer than a sentence
  // or two wouldn't land): the DOM owns the text — fast chunked pastes can't be
  // dropped by a controlled re-render — and the form reads it on submit.
  const inputRef = useRef(null);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);
  const readValue = () => (inputRef.current ? inputRef.current.value : "");

  // Voice-memo handoff (Jeff 10/29) — same contract, uncontrolled version:
  // the mic pours text straight into the DOM box. registerFill hands this
  // setter to the mic component without a second form.
  useEffect(() => {
    registerFill?.((text) => {
      const el = inputRef.current;
      if (!el) return;
      const prev = el.value;
      el.value = prev ? `${prev}\n\n${text}` : text;
      setCount(el.value.length);
      if (el.value.length > LIMIT) el.value = el.value.slice(0, LIMIT);
    });
  }, [registerFill]);

  const submit = async (e) => {
    e.preventDefault();
    const t = readValue().trim();
    if (!t || busy) return;
    setBusy(true);
    setError(null);
    let res = null;
    let failure = null;
    try {
      const ids = registerAttachIds?.() || [];
      res = await addWork(t, null, ids);
    } catch (err) {
      failure = err;
    }
    // Every state write lands AFTER the await (react-19 flushes reliably here —
    // the inline-try pattern left the success confirm unrendered in tests and,
    // on the live site, could drop the confirmation after a dictation submit).
    if (failure) {
      setError(failure.message || "That didn't go through — try again.");
    } else if (res) {
      if (inputRef.current) inputRef.current.value = "";
      setCount(0);
      setDone("Added — it's on the board as a new idea. Kit picks it up from here.");
      onAdded?.(res);
    }
    setBusy(false);
  };

  // One path for typing AND pasting (both arrive as input events): if the
  // incoming text blows past the limit, keep what fits and SAY what was cut.
  const onChange = (e) => {
    // Same contract as before, but on the DOM box: an over-limit dump is kept
    // to the cap and the cut is SAID, never swallowed silently.
    const el = e.target;
    if (el.value.length > LIMIT) {
      const cut = el.value.length - LIMIT;
      el.value = el.value.slice(0, LIMIT);
      setCount(LIMIT);
      setError(`Big dump — kept the first ${LIMIT.toLocaleString()} characters and cut ${cut.toLocaleString()}. Send the rest as a second note (or say "continue") and Kit stitches them together.`);
    } else {
      setCount(el.value.length);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="cc-add-work"
      className={cn(
        "flex flex-col gap-2 rounded-card border border-os-border px-5 py-3",
        tall && "h-full",
      )}
    >
      <textarea
        data-testid="cc-add-work-input"
        ref={inputRef}
        onChange={onChange}
        rows={tall ? 7 : 3}
        placeholder={"What should Kit start? Type it in plain words…\nDictate or paste as much as you want — it all goes to the board."}
        className={cn(
          "w-full bg-transparent text-[0.9375rem] leading-relaxed outline-none placeholder:text-os-ink-3",
          tall ? "min-h-[10.5rem]" : "min-h-[6.5rem] resize-y",
        )}
      />
      <div className="flex items-center justify-between gap-2">
        <button
          type="submit"
          data-testid="cc-add-work-button"
          disabled={busy || count === 0}
          className="rounded-md border border-os-border px-3 py-1.5 font-mono text-[0.75rem] text-os-ink hover:border-os-border-strong disabled:opacity-50"
        >
          {busy ? "Adding…" : "Start it"}
        </button>
        <div className="flex items-center gap-3">
          {count > 0 && (
            <span data-testid="cc-add-work-count" className="text-[0.75rem] font-mono text-os-ink-3">
              {count.toLocaleString()} / {LIMIT.toLocaleString()}
            </span>
          )}
          {done && <span data-testid="cc-add-work-done" className="text-[0.8125rem] text-os-green">{done}</span>}
        </div>
      </div>
      {error && (
        <p data-testid="cc-add-work-error" role="alert" className="text-[0.8125rem] font-medium text-os-red">
          {error}
        </p>
      )}
    </form>
  );
};

export default AddWorkBar;